/**
 * useScrollColumn — one hook powering both dashboard-column behaviours:
 *
 *  1. Scroll memory: remembers each column's scrollTop keyed by `key` in an
 *     in-memory Map, so flicking between tabs restores where you were. Pass
 *     the pathname as the key for the main column (per-page memory); pass a
 *     constant like "left-sidebar" for the rails (global memory).
 *  2. Scroll cue: toggles `data-fade-top` / `data-fade-bottom` attributes so
 *     the `.col-fade` CSS fades the top/bottom edge only when there is more
 *     content to scroll that way.
 *
 * Restore is retried on a short schedule because page content loads async
 * (lazy chunks + data fetches) — a single restore on mount would land short.
 */
import { useEffect, useRef } from "react";

const store = new Map();
const RETRY_DELAYS = [0, 80, 180, 320, 520, 800, 1200];

function paintFade(el) {
    if (!el) return;
    el.toggleAttribute("data-fade-top", el.scrollTop > 4);
    el.toggleAttribute("data-fade-bottom", el.scrollTop + el.clientHeight < el.scrollHeight - 4);
}

export function useScrollColumn(key) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const saved = store.get(key) ?? 0;
        let userMoved = false;
        let settled = false;

        const attempt = () => {
            if (userMoved || settled) return;
            el.scrollTop = saved;
            if (Math.abs(el.scrollTop - saved) < 2) settled = true;   // content tall enough now
            paintFade(el);
        };
        // Ignore the clamped scroll events fired by our own restore attempts —
        // only persist once we've settled or the user has taken over.
        const onScroll = () => { if (settled || userMoved) store.set(key, el.scrollTop); paintFade(el); };
        const onUser = () => { userMoved = true; store.set(key, el.scrollTop); };

        attempt();
        const timers = RETRY_DELAYS.map((t) => setTimeout(attempt, t));
        el.addEventListener("scroll", onScroll, { passive: true });
        el.addEventListener("wheel", onUser, { passive: true });
        el.addEventListener("touchstart", onUser, { passive: true });
        el.addEventListener("keydown", onUser);

        return () => {
            timers.forEach(clearTimeout);
            el.removeEventListener("scroll", onScroll);
            el.removeEventListener("wheel", onUser);
            el.removeEventListener("touchstart", onUser);
            el.removeEventListener("keydown", onUser);
            store.set(key, el.scrollTop);   // persist final position on unmount
        };
    }, [key]);
    return ref;
}
